// Session related enums
export enum SessionStatus {
  Unmatched = 0,
  Unverified = 1,
  Verified = 2,
  Active = 3,
  Matched = 4,
}

export enum SessionSource {
  Web = 0,
  Email = 1,
  Phone = 2,
  SMS = 3,
  API = 4,
}

export enum SessionResult {
  Active = 0,
  Timeout = 1,
  Abandoned = 2,
  Complete = 3,
  LifelineTimeout = 4,
  LifelineUndo = 5,
  LifelineExit = 6,
  Error = 7,
  LifelineContactFailed = 8,
  Logout = 9,
  Bypass = 10,
  InvalidToken = 11,
  LifelineError = 12,
  ExpiredToken = 13,
  FrequencyBypass = 14,
  UnmatchedBypass = 15,
  LifelineSearch = 16,
  LifelineNoMatch = 17,
  SmsStopBypass = 18,
  SmsErrorBypass = 19,
  AutoBypass = 20,
  SmsBlockedBypass = 21,
  LifelineNoVerify = 22,
  LifelineLocked = 23,
  LifelineSubscription = 24,
}

// MFA related enums
export enum MFAStatus {
  Disabled = 1,
  Pending = 2,
  Failed = 3,
  Success = 4,
}

export enum MFAMethod {
  Email = 1,
  SMS = 2,
  Voice = 3,
}

// Lifeline related enums
export enum LifelineType {
  Phone = 1,
  Email = 2,
  Chat = 3,
}

// Item status enums
export enum ItemStatus {
  Pending = 0,
  Trial = 1,
  Active = 2,
  Cancelled = 3,
  RecycleBillingFailed = 4,
  RecycleBilling = 5,
  Refunded = 6,
  Declined = 7,
  Complete = 8,
  Replaced = 9,
  Paused = 10,
  ThirdPartyCancel = 11,
  RecycleFailedHold = 12,
  ThirdPartyManaged = 13,
}

// Pre-match state — set by backend when the customer has been pre-identified
// (e.g. via deep-link query params) but customer match has not completed yet.
export enum PreMatchStatus {
  None = 0,
  Pending = 1,
  Matched = 2,
  Unmatched = 3,
}

// Subdomain entry mode — mirrors the backend SessionType enum returned as
// `campaign.subdomain_type` from /session/start/. Used to branch home/
// breadcrumb/logo affordances and route entry per subdomain.
export enum SubdomainType {
  LegacySupport = 1,
  Marketplace = 2,
  PaymentUpdate = 3,
  CustomerPortal = 4,
  SupportPortal = 5,
  HelpDesk = 6,
  Retention = 7,
  RetentionDemo = 8,
}

export enum CustomerPortalInputType {
  CustomerPortalHome = 30,
  CustomerPortalOrders = 31,
  CustomerPortalSubscriptions = 32,
  CustomerPortalSubscriptionDetail = 33,
  CustomerPortalPersonalInfo = 34,
  CustomerPortalOrderDetail = 35,
}

export enum QuantityType {
  Any = 1,
  None = 2,
  LowerOnly = 3,
  HigherOnly = 4,
}

// Input types enum
export enum InputType {
  Choice = 0,
  Text = 1,
  Phone = 2,
  Item = 3,
  ActiveItem = 4,
  Product = 5,
  Faqs = 6,
  BillingAddress = 7,
  ShippingAddress = 8,
  ItemStatus = 9,
  InputActionStatus = 10,
  Radio = 11,
  Select = 12,
  NextSelectedItem = 13, // deprecated
  RadioSelect = 14,
  Hidden = 15,
  EnterProductFunnel = 16,
  PaymentInfo = 17,
  Link = 18,
  EnterTroubleshooter = 19,
  ContactCallCenter = 20,
  SelectCampaign = 21,
  MatchCustomer = 22,
  Date = 23,
  Checkout = 24,
  CheckoutConfirm = 25,
  RepeatActionHoldoff = 26,
  ShipmentTracking = 27,
  Confirmation = 28,
  ConfirmationNo = 29,
  CustomerPortalHome = 30,
  CustomerPortalOrders = 31,
  CustomerPortalSubscriptions = 32,
  CustomerPortalSubscriptionDetail = 33,
  CustomerPortalPersonalInfo = 34,
  CustomerPortalOrderDetail = 35,
  RepeatActionHoldoffResponse = 36,
  SubscriptionItem = 37,
  ChangeQuantity = 1001,
  ChangeInterval = 1002,
  Cancel = 1003,
  NoActiveItems = 1004,
  ChangeProduct = 1005,
  Return = 1006,
  Billing = 1007,
  AltChildExit = 1008,
  PathNotFound = 1009,
  NoCampaigns = 1010,
  CustomerNotFound = 1011,
  RebillItem = 1012,
  ShipmentReturnLabel = 1013,
  ShipmentReturnQRCode = 1014,
  PauseSubscription = 1015,
  SkipSubscription = 1016,
  ReactivateSubscription = 1017,
  CancelSubscription = 1018,
  CancelTrial = 1019,
  CancelSubscriptionHold = 1020,
  CancelTrialHold = 1021,
  ReturnSubscription = 1022,
  ReturnTrial = 1023,
  CallCenterClosed = 1024,
  NoChange = 1025,
  ChangeVariant = 1026,
  ChangeSubscriptionProduct = 1027,
  ChangeSubscriptionVariant = 1028,
  CancelRebillHold = 1029,
  OfferDetails = 1030,
  BillNow = 1031,
  ShippingProcessing = 1032,
  ReturnPending = 1033,
}

// Preview type
export enum PreviewType {
  None = 0,
  Funnel = 1,
  Step = 2,
}

// Step categories (classify steps for intent hierarchy and analytics)
export enum StepCategory {
  Custom = 2000,
  Reasons = 2001,
  OrderOptions = 2002,
  DiscountFutureOrder = 2003,
  RefundOrder = 2004,
  RmaDownsell = 2005,
  UpsellOrder = 2006,
  PauseSub = 2007,
  OfferDetails = 2008,
  ReviseOrder = 2009,
  NeedMoreHelp = 2010,
  AdjustShippingFrequency = 2011,
  ReturnPolicy = 2012,
  RefundWithDiscount = 2013,
  DiscountWithAdjustShippingFrequency = 2014,
  ReactivateSubscription = 2015,
  ReactivateSubscriptionWithDiscount = 2016,
  Survey = 2017,
  FixedAmountDownsell = 2018,
  PercentageDownsell = 2019,
  LossAversion = 2020,
  TimeForFree = 2021,
  ListLossAversion = 2022,
  Testimonial = 2023,
  ExtendTrial = 2024,
  DiscountedUpsell = 2025,
  SpecialOffer = 2026,
  CancelSurvey = 2027,
  ReturnSurvey = 2028,
  Feedback = 2029,
  ScheduleSupport = 2030,
  LifelineStep = 2031,
  Handoff = 2032,
  PauseSurvey = 2033,
  CancelTrialSurvey = 2034,
  CancelSubSurvey = 2035,
  RMA = 2036,
  ChangeVariant = 2037,
  SkipSubscription = 2038,
}

// Billing cycle types
export enum BillingCycleType {
  OneTime = 0,
  Recurring = 1,
  MultiPay = 2,
  RecurringChangeProduct = 3,
}

// Home-page moment (OMP). `None` is the editor's "Default" — its sections_config key is `default`.
export enum Moment {
  None = 0,
  OrderPending = 1,
  OrderShipped = 2,
  SubscriptionActive = 3,
  SubscriptionCancelled = 4,
  PaymentIssue = 5,
}

export type MomentSlug =
  'default' | 'order_pending' | 'order_shipped' | 'subscription_active' | 'subscription_cancelled' | 'payment_issue'

export const MOMENT_SLUGS: Record<number, MomentSlug> = {
  [Moment.None]: 'default',
  [Moment.OrderPending]: 'order_pending',
  [Moment.OrderShipped]: 'order_shipped',
  [Moment.SubscriptionActive]: 'subscription_active',
  [Moment.SubscriptionCancelled]: 'subscription_cancelled',
  [Moment.PaymentIssue]: 'payment_issue',
}

// Keys of the portal editor's `sections_config` payload.
export const PORTAL_PAGES = {
  Home: 'home',
  Subscriptions: 'subscriptions',
  SubscriptionDetails: 'subscription_details',
  Orders: 'orders',
  OrderDetails: 'order_details',
} as const

// Fulfillment status
export enum FulfillmentStatus {
  Hold = 0,
  Shipped = 1,
  Delivered = 2,
  RMAPending = 3,
  Returned = 4,
  Cancelled = 5,
  Processing = 6,
  PendingShipment = 7,
  Failed = 8,
  None = 9,
  NextCycle = 10,
  Removed = 11,
  ProcessingReattempt = 12,
  Skipped = 13,
  ParentOrder = 14,
  Frozen = 15,
}

// Offer intent types — backend canonical identifiers for path intents.
// Used in the retention modal payload so the modal can route to the right offer.
export enum OfferIntentType {
  Legacy = 0,
  Cancel = 1,
  Return = 2,
  Upsell = 3,
  CancelSubscription = 4,
  CancelTrial = 5,
  Other = 6,
  AdjustShippingFrequency = 7,
  PauseSubscription = 8,
  ReviseOrder = 9,
  ReactivateSubscription = 10,
  CancelSubscriptionHold = 11,
  CancelTrialHold = 12,
  OfferDetails = 13,
  ReturnSubscription = 14,
  ReturnTrial = 15,
  ChangeVariant = 16,
  ChangeSubscriptionProduct = 17,
  ChangeSubscriptionVariant = 18,
  SkipSubscription = 19,
  CancelRebillHold = 20,
  ShipmentTracking = 21,
  ItemStatus = 22,
  FixedProductRevision = 23,
  BillNow = 24,
  RebillItem = 25,
  ShippingProcessing = 26,
  ReturnPending = 27,
}

// Item pending status
export enum ItemPendingStatus {
  NotPending = 0,
  Cancel = 1,
  Return = 2,
  Exchange = 3,
  CancelSubscription = 4,
}

// Tracking status
export enum TrackingStatus {
  Unknown = 0,
  PreTransit = 1,
  InTransit = 2,
  OutForDelivery = 3,
  Delivered = 4,
  AvailableForPickup = 5,
  ReturnToSender = 6,
  Failure = 7,
  Cancelled = 8,
  Error = 9,
  NotFound = 10,
  Expired = 11,
}

// Payment source
export enum PaymentSource {
  CreditCard = 0,
  Check = 1,
  PrePaid = 2,
  COD = 3,
  DirectDebit = 4,
  PayPal = 5,
  ApplePay = 6,
  GooglePay = 7,
  Other = 8,
  Shopify = 9,
}

// Transaction response
export enum TranResponse {
  Success = 0,
  SoftDecline = 1,
  HardDecline = 2,
}

// Funnel item status
export enum FunnelItemStatus {
  InActive = -1,
  Active = 0,
  InTrial = 1,
  Hold = 2,
  Processing = 3,
  OneTimeShipped = 4,
  OneTimeInTrial = 5,
}

// Input state
export enum InputState {
  Unknown = 0,
  OkToSubmit = 1,
  NeedsMatch = 2,
  NeedsVerification = 3,
  OrderItemStatus = 4,
  InputFields = 5,
  RMAItems = 6,
  Variants = 7,
  RebillItems = 8,
}

// Product status
export enum ProductStatus {
  Normal = 0,
  ReadOnly = 1,
  Hidden = 2,
  Cancellable = 3,
  HideIfFreeFirstCycle = 4,
}

// Fulfillment type
export enum FulfillmentType {
  Standard = 0,
  NoShipping = 1,
}

// Product type
export enum ProductType {
  Offer = 0,
  Upsale = 1,
}

// Process type
export enum ProcessType {
  Sale = 1,
  Fee = 2,
  Warranty = 3,
  Installment = 4,
}

// Variant type
export enum VariantType {
  None = 0,
  Standard = 1,
  Bundled = 2,
}

// Cancel items selection
export enum CancelItemsSelection {
  ShowAll = 1,
  ShowNone = 2,
  UpsellsOnly = 3,
  UpsellsOnlyIfUpsell = 4,
}

// Order payment options
export enum OrderPaymentOptions {
  Default = 0,
  New = 1,
}

// Subscription display type
export enum SubscriptionDisplayType {
  Never = 1,
  Always = 2,
  AfterFirstCycle = 3,
}

// Mobile icon button type
export enum MobileIconButtonType {
  Tile = 1,
  Box = 2,
}

// Payment update allowed
export enum PaymentUpdateAllowed {
  NO = 0,
  YES = 1,
  EXPIRY_ONLY = 2,
}

// Step button type
export enum StepButtonType {
  Flat = 0,
  Modern = 1,
  Pill = 2,
  FlatOutline = 3,
  ModernOutline = 4,
  PillOutline = 5,
  TextOnly = 6,
}

// Checkout steps
export enum CheckoutSteps {
  SelectItems = 1,
  AddressInfo = 2,
  PaymentInfo = 3,
  OrderPlaced = 4,
}

// Verbs accepted by the session action endpoint.
export const SessionAction = {
  Verify: 'verify',
  Match: 'match',
  Input: 'input',
  Jump: 'jump',
  Email: 'email',
  Undo: 'undo',
  Close: 'close',
  MfaSend: 'mfa_send',
  MfaVerify: 'mfa_verify',
  Home: 'home',
} as const

export type SessionActionVerb = (typeof SessionAction)[keyof typeof SessionAction]

// Popup close setting
export enum PopupCloseSetting {
  NoClose = 1,
  Undo = 2,
  FirstInput = 3,
  LastInput = 4,
  UndoOrFirstInput = 5,
  UndoOrLastInput = 6,
}

// DOM input types. Distinct from FieldTypes: `phone` and `tel` both map to `tel`.
export const HtmlInputType = {
  Text: 'text',
  Email: 'email',
  Tel: 'tel',
  Number: 'number',
  Password: 'password',
  Date: 'date',
} as const

export type HtmlInputType = (typeof HtmlInputType)[keyof typeof HtmlInputType]

// Image render sizes; keys of the ImageSizes dimension map.
export const ImageSize = {
  Small: 'small',
  Medium: 'medium',
  Large: 'large',
  Original: 'original',
} as const

export type ImageSize = (typeof ImageSize)[keyof typeof ImageSize]

// Values from the portal editor's payload.
export const SectionKey = {
  Header: 'header',
  AnnouncementBar: 'announcement_bar',
  WelcomeMessage: 'welcome_message',
  NavigationalBlocks: 'navigational_blocks',
  NextOrderDetails: 'next_order_details',
  OrderSummary: 'order_summary',
  SubscriptionList: 'subscription_list',
  OrderList: 'order_list',
  CrossSellCarousel: 'cross_sell_carousel',
  ImageBanner: 'image_banner',
  FaqSection: 'faq_section',
  TextBox: 'text_box',
  ShippingAndPayment: 'shipping_and_payment',
  Instagram: 'instagram',
  Footer: 'footer',
} as const

export type SectionKey = (typeof SectionKey)[keyof typeof SectionKey]

// `campaign.match_fields` — how a customer identifies themselves.
export const MatchField = {
  Email: 'email',
  Phone: 'phone',
  OrderNumber: 'order_number',
} as const

export type MatchField = (typeof MatchField)[keyof typeof MatchField]

export const StorageType = {
  Local: 'localStorage',
  Session: 'sessionStorage',
} as const

export type StorageType = (typeof StorageType)[keyof typeof StorageType]

export const Theme = {
  Light: 'light',
  Dark: 'dark',
} as const

export type Theme = (typeof Theme)[keyof typeof Theme]

// Presentation tone for a status badge, derived from ItemStatus / status classes.
export const StatusTone = {
  Active: 'active',
  Processing: 'processing',
  Inactive: 'inactive',
  Neutral: 'neutral',
} as const

export type StatusTone = (typeof StatusTone)[keyof typeof StatusTone]

// Where an item action button renders; from `<action>_button_placement`.
export const ActionSlot = {
  OrderControls: 'order-controls',
  PageBottom: 'page-bottom',
} as const

export type ActionSlot = (typeof ActionSlot)[keyof typeof ActionSlot]

export const ActionVariant = {
  Primary: 'primary',
  Secondary: 'secondary',
  Tertiary: 'tertiary',
} as const

export type ActionVariant = (typeof ActionVariant)[keyof typeof ActionVariant]

export const HeadingSize = {
  Small: 'small',
  Medium: 'medium',
  Large: 'large',
} as const

export type HeadingSize = (typeof HeadingSize)[keyof typeof HeadingSize]

export const PillStyle = {
  LowFillPill: 'low_fill_pill',
  FillPill: 'fill_pill',
  OutlinePill: 'outline_pill',
  Text: 'text',
} as const

export type PillStyle = (typeof PillStyle)[keyof typeof PillStyle]

export const ButtonStyle = {
  Fill: 'fill',
  LowFill: 'low-fill',
  Outline: 'out-line',
  Text: 'text',
} as const

export type ButtonStyle = (typeof ButtonStyle)[keyof typeof ButtonStyle]

export const CornerRoundness = {
  FullyRounded: 'fully-rounded',
  GentlyRounded: 'gently-rounded',
  Square: 'square',
} as const

export type CornerRoundness = (typeof CornerRoundness)[keyof typeof CornerRoundness]

export const BorderStyle = {
  Solid: 'solid',
  Dashed: 'dashed',
  Dotted: 'dotted',
  None: 'none',
} as const

export type BorderStyle = (typeof BorderStyle)[keyof typeof BorderStyle]

// Visitor hardware, not ContentWidthDeviceSize (a campaign setting).
export enum DeviceType {
  Mobile = 'mobile',
  Tablet = 'tablet',
  Desktop = 'desktop',
}

// Content width device size
export enum ContentWidthDeviceSize {
  Mobile = 'mobile',
  Tablet = 'tablet',
  Desktop = 'desktop',
}

// Label types
export enum LabelTypes {
  SubscriptionStatus = '1',
}

// Show breadcrumbs
export enum ShowBreadcrumbs {
  OnlyIfCanUndo = 1,
  Always = 2,
  Never = 3,
}

// Verification method (from VerificationField component)
export enum VerificationMethod {
  FullName = 1,
  LastName = 2,
  Street = 3,
  Zip = 4,
  Address = 5,
  BillingStreet = 6,
  BillingZip = 7,
  BillingAddress = 8,
  Phone = 1000,
  PartialPhone = 1001,
  Email = 1002,
  PartialEmail = 1003,
}

// Status display state (from InputActionStatus component)
export enum StatusDisplayState {
  Status = 0,
  ItemDetails = 1,
  Destroyed = 2,
}
